import api from '@functions/api'

import type { CreditSummaryByStatusModel, CreditSummaryModel } from '@models/creditSummary'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/creditSummary/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/creditSummary/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/creditSummary/FETCH_FAILURE'

export type CreditSummaryModuleState = {
  errors: string[],
  loaded: boolean,
  loading: boolean,
  result: CreditSummaryModel | {},
}

const initialState: CreditSummaryModuleState = {
  errors: [],
  loaded: false,
  loading: false,
  result: {},
}

// Actions
export function fetchRequest(): Action {
  return {
    type: FETCH_REQUEST,
  }
}

type FetchRequestPayload = {
  result: CreditSummaryModel,
  type: string,
}

export function fetchSuccess(payload: FetchRequestPayload): Action {
  return {
    type: FETCH_SUCCESS,
    result: payload.result,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

// Action Creators
export function fetchCreditSummary() {
  return (dispatch: AppDispatch): Promise<ActionResult> => {
    dispatch(fetchRequest())

    const promise = api('/credits/summary')
      .then(({ data }) => {
        dispatch(fetchSuccess({ result: data }))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function fetchCreditSummaryByStatus(options: EntityOptions) {
  return (dispatch: AppDispatch): Promise<ActionResult<CreditSummaryByStatusModel>> => {
    dispatch(fetchRequest())

    const promise = api('/credits/list-per-status', options)
      .then(({ data }) => {
        dispatch(fetchSuccess({ result: data }))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CreditSummaryModuleState = initialState,
  action: Action = { type: '' },
): CreditSummaryModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        errors: [],
        loaded: true,
        loading: false,
        result: action.result || state.result,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        errors: action.errors || [],
        loaded: false,
        loading: false,
      }
    default:
      return state
  }
}
